/**
 * 权限覆盖管理面板
 * 支持对用户进行权限授予(grant)或拒绝(deny)的个性化覆盖
 */
import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Space, Input, Form, Select,
  Modal, message, Tag, Popconfirm, DatePicker,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, ExclamationCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import PermissionTraceDrawer from './PermissionTraceDrawer';
import {
  getPermissionOverrideList,
  createPermissionOverride,
  updatePermissionOverride,
  deletePermissionOverride,
} from '../../api/permissionEngine';
import type {
  PermissionOverrideInfo,
  PermissionOverrideQueryParams,
  CreatePermissionOverrideData,
  UpdatePermissionOverrideData,
} from '../../api/types';

const { confirm } = Modal;

const PermissionOverridePanel: React.FC = () => {
  const [list, setList] = useState<PermissionOverrideInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [editingOverride, setEditingOverride] = useState<PermissionOverrideInfo | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [traceDrawerOpen, setTraceDrawerOpen] = useState(false);
  const [traceUserId, setTraceUserId] = useState<number | undefined>();
  const [tracePermCode, setTracePermCode] = useState<string | undefined>();
  const [searchForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const fetchList = async () => {
    setLoading(true);
    try {
      const formValues = searchForm.getFieldsValue();
      const params: PermissionOverrideQueryParams = {
        ...formValues,
        page: pagination.current,
        pageSize: pagination.pageSize,
      };

      const res = await getPermissionOverrideList(params);
      if (res?.data) {
        const responseData = res.data as any;
        setList(responseData.list || []);
        setTotal(responseData.total || 0);
      }
    } catch (error) {
      console.error('获取权限覆盖列表失败', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, [pagination.current, pagination.pageSize]);

  const handleTableChange = (paginationInfo: any) => {
    setPagination({
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    });
  };

  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    fetchList();
  };

  const handleReset = () => {
    searchForm.resetFields();
    setPagination({ ...pagination, current: 1 });
    fetchList();
  };

  const handleAdd = () => {
    setEditingOverride(null);
    editForm.resetFields();
    setFormModalVisible(true);
  };

  const handleEdit = (record: PermissionOverrideInfo) => {
    setEditingOverride(record);
    editForm.setFieldsValue({
      userId: record.userId,
      permissionCode: record.permissionCode,
      overrideType: record.overrideType,
      reason: record.reason,
      expiresAt: record.expiresAt ? dayjs(record.expiresAt) : undefined,
      status: record.status,
    });
    setFormModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await deletePermissionOverride(id);
      message.success('删除成功');
      fetchList();
    } catch (error) {
      console.error('删除失败', error);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await editForm.validateFields();
      setSubmitting(true);

      const expiresAt = values.expiresAt ? values.expiresAt.toISOString() : undefined;

      if (editingOverride) {
        const updateData: UpdatePermissionOverrideData = {
          overrideType: values.overrideType,
          reason: values.reason,
          expiresAt,
          status: values.status,
        };
        await updatePermissionOverride(editingOverride.id, updateData);
        message.success('更新成功');
      } else {
        const createData: CreatePermissionOverrideData = {
          userId: values.userId,
          permissionCode: values.permissionCode,
          overrideType: values.overrideType,
          reason: values.reason,
          expiresAt,
        };
        await createPermissionOverride(createData);
        message.success('创建成功');
      }

      setFormModalVisible(false);
      fetchList();
    } catch (error: any) {
      if (error?.errorFields) return; // form validation error
      console.error('操作失败', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (record: PermissionOverrideInfo) => {
    try {
      await updatePermissionOverride(record.id, {
        status: record.status === 1 ? 0 : 1,
      });
      message.success(record.status === 1 ? '已禁用' : '已启用');
      fetchList();
    } catch (error) {
      console.error('状态更新失败', error);
    }
  };

  const columns: ColumnsType<PermissionOverrideInfo> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 70,
      fixed: 'left',
    },
    {
      title: '用户',
      dataIndex: 'userName',
      width: 120,
      render: (name, record) => name || `用户#${record.userId}`,
    },
    {
      title: '权限码',
      dataIndex: 'permissionCode',
      width: 200,
      ellipsis: true,
    },
    {
      title: '覆盖类型',
      dataIndex: 'overrideType',
      width: 100,
      render: (type: string) => (
        <Tag color={type === 'grant' ? 'success' : 'error'}>
          {type === 'grant' ? '授予' : '拒绝'}
        </Tag>
      ),
    },
    {
      title: '原因',
      dataIndex: 'reason',
      width: 200,
      ellipsis: true,
      render: (reason) => reason || '-',
    },
    {
      title: '授权人',
      dataIndex: 'grantedByName',
      width: 120,
      render: (name, record) => name || `用户#${record.grantedBy}`,
    },
    {
      title: '过期时间',
      dataIndex: 'expiresAt',
      width: 160,
      render: (expiresAt) => {
        if (!expiresAt) return <Tag>永久</Tag>;
        const isExpired = dayjs(expiresAt).isBefore(dayjs());
        return (
          <Tag color={isExpired ? 'default' : 'blue'}>
            {dayjs(expiresAt).format('YYYY-MM-DD HH:mm')}
          </Tag>
        );
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 80,
      render: (status) => (
        <Tag color={status === 1 ? 'success' : 'default'}>
          {status === 1 ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      width: 160,
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              setTraceUserId(record.userId);
              setTracePermCode(record.permissionCode);
              setTraceDrawerOpen(true);
            }}
          >
            溯源
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => handleToggleStatus(record)}
          >
            {record.status === 1 ? '禁用' : '启用'}
          </Button>
          <Popconfirm
            title="确定要删除此权限覆盖吗?"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger size="small">
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card title="权限覆盖管理" bordered={false}>
        {/* 搜索表单 */}
        <Form
          form={searchForm}
          layout="inline"
          onFinish={handleSearch}
          style={{ marginBottom: 16 }}
        >
          <Form.Item name="userId">
            <Input placeholder="用户ID" allowClear style={{ width: 120 }} />
          </Form.Item>
          <Form.Item name="permissionCode">
            <Input placeholder="权限码" allowClear style={{ width: 180 }} />
          </Form.Item>
          <Form.Item name="overrideType">
            <Select placeholder="覆盖类型" style={{ width: 120 }} allowClear>
              <Select.Option value="grant">授予</Select.Option>
              <Select.Option value="deny">拒绝</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="status">
            <Select placeholder="状态" style={{ width: 100 }} allowClear>
              <Select.Option value={1}>启用</Select.Option>
              <Select.Option value={0}>禁用</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">搜索</Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>

        {/* 工具栏 */}
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              新增覆盖
            </Button>
            <Button icon={<SearchOutlined />} onClick={() => setTraceDrawerOpen(true)}>
              权限溯源
            </Button>
          </Space>
        </div>

        {/* 表格 */}
        <Table
          rowKey="id"
          columns={columns}
          dataSource={list}
          pagination={{
            ...pagination,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (t) => `共 ${t} 条`,
          }}
          loading={loading}
          onChange={handleTableChange}
          scroll={{ x: 1400 }}
        />
      </Card>

      {/* 新增/编辑弹窗 */}
      <Modal
        title={editingOverride ? '编辑权限覆盖' : '新增权限覆盖'}
        open={formModalVisible}
        onOk={handleSubmit}
        onCancel={() => setFormModalVisible(false)}
        confirmLoading={submitting}
        destroyOnHidden
        width={520}
      >
        <Form form={editForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="userId"
            label="用户ID"
            rules={[{ required: true, message: '请输入用户ID' }]}
          >
            <Input
              type="number"
              placeholder="输入目标用户ID"
              disabled={!!editingOverride}
            />
          </Form.Item>
          <Form.Item
            name="permissionCode"
            label="权限码"
            rules={[{ required: true, message: '请输入权限码' }]}
          >
            <Input
              placeholder="如: user:create, order:delete"
              disabled={!!editingOverride}
            />
          </Form.Item>
          <Form.Item
            name="overrideType"
            label="覆盖类型"
            rules={[{ required: true, message: '请选择覆盖类型' }]}
          >
            <Select placeholder="选择覆盖类型">
              <Select.Option value="grant">授予 - 额外授予该权限</Select.Option>
              <Select.Option value="deny">拒绝 - 明确拒绝该权限</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="reason" label="原因说明">
            <Input.TextArea placeholder="说明覆盖原因（可选）" rows={3} />
          </Form.Item>
          <Form.Item name="expiresAt" label="过期时间">
            <DatePicker
              showTime
              placeholder="留空表示永久生效"
              style={{ width: '100%' }}
            />
          </Form.Item>
          {editingOverride && (
            <Form.Item name="status" label="状态">
              <Select>
                <Select.Option value={1}>启用</Select.Option>
                <Select.Option value={0}>禁用</Select.Option>
              </Select>
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* 权限溯源抽屉 */}
      <PermissionTraceDrawer
        open={traceDrawerOpen}
        onClose={() => {
          setTraceDrawerOpen(false);
          setTraceUserId(undefined);
          setTracePermCode(undefined);
        }}
        defaultUserId={traceUserId}
        defaultPermissionCode={tracePermCode}
      />
    </div>
  );
};

export default PermissionOverridePanel;
